import Accordion from '@/common/components/Accordion'
import Button from '@/common/components/Button'
import MaskedTextField from '@/common/components/MaskedTextField'
import SelectController from '@/common/components/SelectController'
import TextField from '@/common/components/TextField'
import { useEquipmentCategories } from '@/common/hooks/queries/useEquipmentCategories'
import useDebounce from '@/common/hooks/useDebounce'
import { Mapper } from '@/common/services/mapper'
import { EquipmentCategoryData } from '@/features/equipmentModels/types/equipmentCategories'
import { useCreatePublicCompanyLeadContractEquipmentCategory } from '@/features/publicCompanyLeadsFunnel/hooks/useCreatePublicCompanyLeadContractEquipmentCategory'
import { useDeletePublicCompanyLeadContractEquipmentCategory } from '@/features/publicCompanyLeadsFunnel/hooks/useDeletePublicCompanyLeadContractEquipmentCategory'
import { useUpdatePublicCompanyLeadContractEquipmentCategory } from '@/features/publicCompanyLeadsFunnel/hooks/useUpdatePublicCompanyLeadContractEquipmentCategory'
import { PublicCompanyLeadsFunnelUpdateContractValidationSchema } from '@/features/publicCompanyLeadsFunnel/schemas/update.schema'
import { ContractData } from '@/features/publicCompanyLeadsFunnel/types/publicCompanyLead'
import { useIsFetching, useQueryClient } from '@tanstack/react-query'
import { XIcon } from 'lucide-react'
import { Dispatch, SetStateAction } from 'react'
import { Controller, useFormContext } from 'react-hook-form'
import { useParams } from 'react-router-dom'

interface ContractEquipmentsProps {
  contract?: ContractData
  setServerError: Dispatch<SetStateAction<boolean>>
  disabled?: boolean
}

export const ContractEquipments = ({ contract, setServerError, disabled }: ContractEquipmentsProps) => {
  const { publicCompanyLeadId } = useParams()
  const queryClient = useQueryClient()

  const [searchEquipmentCategory, setSearchEquipmentCategory] = useDebounce('')

  const publicCompanyIsFetching = useIsFetching({ queryKey: ['publicCompanyLead', publicCompanyLeadId] })

  const {
    control,
    setValue,
    formState: { errors },
  } = useFormContext<PublicCompanyLeadsFunnelUpdateContractValidationSchema>()

  const { data: equipmentCategoryOptions } = useEquipmentCategories({
    filters: { search: searchEquipmentCategory },
    mapper: Mapper.mapToOptions<EquipmentCategoryData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  })

  const onSuccess = () => {
    setServerError(false)
    queryClient.invalidateQueries({ queryKey: ['publicCompanyLead', publicCompanyLeadId] })
  }

  const {
    mutate: createPublicCompanyLeadContractEquipmentCategory,
    isPending: createPublicCompanyLeadContractEquipmentCategoryIsPending,
  } = useCreatePublicCompanyLeadContractEquipmentCategory({
    publicCompanyLeadId,
    onSuccess: () => {
      setValue('contract.contract_equipment_categories', null)
      onSuccess()
    },
    onError: () => {
      setValue('contract.contract_equipment_categories', null)
      setServerError(true)
    },
  })

  const {
    mutate: updatePublicCompanyLeadContractEquipmentCategory,
    isPending: updateContractEquipmentCategoryIsPending,
  } = useUpdatePublicCompanyLeadContractEquipmentCategory({
    publicCompanyLeadId,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['publicCompanyLead', publicCompanyLeadId] })
    },
  })

  const {
    mutate: deletePublicCompanyLeadEquipmentCategory,
    isPending: deletePublicCompanyLeadEquipmentCategoryIsPending,
  } = useDeletePublicCompanyLeadContractEquipmentCategory({
    publicCompanyLeadId,
    onSuccess,
    onError: () => setServerError(true),
  })

  return (
    <Accordion
      name='equipments'
      className="py-1"
      triggerDisabled={updateContractEquipmentCategoryIsPending}
      head={<p>Equipamentos <span className="text-danger-soft"> *</span></p>}
      body={
        <div className="flex flex-col gap-4">
          <SelectController
            control={control}
            name="contract.contract_equipment_categories"
            placeholder="Pesquisar Equipamentos"
            value={null}
            triggerOnChange={(newValue: { label: string; value: string }) => {
              createPublicCompanyLeadContractEquipmentCategory(newValue)
            }}
            onInputChange={setSearchEquipmentCategory}
            options={
              equipmentCategoryOptions?.length
                ? equipmentCategoryOptions.filter(
                    (value) =>
                      !contract?.contract_equipment_categories?.find(
                        ({ equipment_category }) => equipment_category.id === value.value,
                      ),
                  )
                : []
            }
            isDisabled={
              !!publicCompanyIsFetching ||
              createPublicCompanyLeadContractEquipmentCategoryIsPending ||
              deletePublicCompanyLeadEquipmentCategoryIsPending ||
              disabled
            }
            isLoading={!!publicCompanyIsFetching || createPublicCompanyLeadContractEquipmentCategoryIsPending}
          />
          {contract?.contract_equipment_categories?.map(({ id, equipment_category, quantity }, index) => (
            <div key={equipment_category.id} className="flex gap-2">
              <Button
                type="button"
                variant="outline-error"
                className="size-[54px] mt-auto"
                onClick={() => deletePublicCompanyLeadEquipmentCategory(id)}
                disabled={
                  !!publicCompanyIsFetching ||
                  updateContractEquipmentCategoryIsPending ||
                  deletePublicCompanyLeadEquipmentCategoryIsPending ||
                  disabled
                }
              >
                <XIcon />
              </Button>
              <TextField label="Categoria" defaultValue={equipment_category.display_name} className="w-1/2" disabled />
              <Controller
                control={control}
                name={`contract.contract_equipment_categories.${index}.quantity`}
                defaultValue={quantity ? `${quantity}` : ''}
                disabled={
                  !!publicCompanyIsFetching ||
                  updateContractEquipmentCategoryIsPending ||
                  deletePublicCompanyLeadEquipmentCategoryIsPending ||
                  disabled
                }
                render={({ field: { onBlur, ...rest } }) => (
                  <MaskedTextField
                    mask="0000"
                    label="Quantidade"
                    markAsRequired
                    className="w-1/2"
                    {...rest}
                    errorMessage={errors?.contract?.contract_equipment_categories?.[index]?.quantity?.message}
                    onBlur={(e) => {
                      onBlur()
                      updatePublicCompanyLeadContractEquipmentCategory({ id, quantity: e.target.value })
                    }}
                  />
                )}
              />
            </div>
          ))}
          <div className="text-right">
            <p className="text-xs font-light italic">Total de equipamentos</p>
            <p className="font-bold">
              {contract?.contract_equipment_categories?.reduce((acc, { quantity }) => acc + quantity, 0)}
            </p>
          </div>
        </div>
      }
    />
  )
}
